import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid2,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useLocales } from 'src/locales';
import Iconify from 'src/components/iconify';
import {
  useCreateTourClassVehicleMatrixMutation,
  useDeleteTourClassVehicleMatrixMutation,
  useGetTourClassVehicleMatricesByTourBundleQuery,
  useUpdateTourClassVehicleMatrixMutation,
} from 'src/redux/slices/tourClassVehicleMatrixSlice';
import { TourClassVehicleMatrix, TourClassCostType } from 'src/@types/tourClassVehicleMatrix';
import { useGetAllVehicleTypesQuery } from 'src/redux/slices/vehicleSlice';
import { useSnackbar } from 'notistack';
import TourClassTimesManagement from './TourClassTimesManagement';
import VehicleCostFeesManagement from './VehicleCostFeesManagement';
import EntityCostFeeManagement from './EntityCostFeeManagement';
import { InfoBox } from '../../../components/map/InfoBox';
import { WarningBox } from '../../../components/map/WarningBox';
import ConfirmDialog from '../../../components/confirm-dialog';

interface Props {
  tourBundleId: string;
}

export default function TourClassVehicleMatrixManagement({ tourBundleId }: Props) {
  const { translate } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMatrix, setEditingMatrix] = useState<TourClassVehicleMatrix | null>(null);
  const [matrixToDelete, setMatrixToDelete] = useState<TourClassVehicleMatrix | null>(null);
  const [hasTourClassTimes, setHasTourClassTimes] = useState(false);
  const [localMatrices, setLocalMatrices] = useState<Partial<TourClassVehicleMatrix>[]>([]);
  const [formData, setFormData] = useState<Partial<TourClassVehicleMatrix>>({
    tourClassCostType: 'DRIVING_PERFORMANCE',
    vehicleTypeId: 0,
    tourBundleId: Number(tourBundleId),
    costFee: [0, 0, 0, 0, 0],
  });

  const { data: matrices, isLoading } =
    useGetTourClassVehicleMatricesByTourBundleQuery(tourBundleId);
  const { data: vehicleTypes } = useGetAllVehicleTypesQuery();
  const [createMatrix] = useCreateTourClassVehicleMatrixMutation();
  const [updateMatrix] = useUpdateTourClassVehicleMatrixMutation();
  const [deleteMatrix, { isLoading: isDeleting }] = useDeleteTourClassVehicleMatrixMutation();

  const costTypes: TourClassCostType[] = ['DRIVING_PERFORMANCE', 'DRIVER', 'ESCORT'];
  const costTypeOrder: Record<TourClassCostType, number> = {
    DRIVING_PERFORMANCE: 0,
    DRIVER: 1,
    ESCORT: 2,
  };

  const handleTourClassTimesChange = (hasTimes: boolean) => {
    setHasTourClassTimes(hasTimes);
  };

  const handleOpenDialog = (matrix?: TourClassVehicleMatrix) => {
    if (!hasTourClassTimes && !matrix) {
      enqueueSnackbar(`${translate('tourClassVehicleMatrix.noTourClassTimesWarning')}`, {
        variant: 'warning',
      });
      return;
    }
    if (matrix) {
      setEditingMatrix(matrix);
      setFormData({ ...matrix });
    } else {
      setEditingMatrix(null);
      setFormData({
        tourClassCostType: 'DRIVING_PERFORMANCE',
        vehicleTypeId: 0,
        tourBundleId: Number(tourBundleId),
        costFee: [0, 0, 0, 0, 0],
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingMatrix(null);
  };

  const handleSave = async () => {
    if (!formData.vehicleTypeId || formData.vehicleTypeId === 0) {
      enqueueSnackbar(`${translate('tourClassVehicleMatrix.selectVehicleType')}`, {
        variant: 'warning',
      });
      return;
    }

    if (editingMatrix) {
      try {
        await updateMatrix({
          id: editingMatrix.id,
          ...formData,
        } as TourClassVehicleMatrix).unwrap();

        enqueueSnackbar(`${translate('tourClassVehicleMatrix.updateSuccess')}`, {
          variant: 'success',
        });
        handleCloseDialog();
      } catch {
        enqueueSnackbar(`${translate('tourClassVehicleMatrix.updateError')}`, {
          variant: 'error',
        });
      }
    } else {
      const vehicleType = vehicleTypes?.find((vt) => vt.id === formData.vehicleTypeId?.toString());

      const newMatrix = {
        ...formData,
        vehicleTypeName: vehicleType?.name || '',
        id: -Date.now(),
      };

      setLocalMatrices([...localMatrices, newMatrix as Partial<TourClassVehicleMatrix>]);
      handleCloseDialog();
      enqueueSnackbar(`${translate('tourClassVehicleMatrix.addedToList')}`, {
        variant: 'success',
      });
    }
  };

  const handleSaveAll = async () => {
    if (localMatrices.length === 0) {
      enqueueSnackbar(`${translate('tourClassVehicleMatrix.noMatricesToSave')}`, {
        variant: 'warning',
      });
      return;
    }

    try {
      for (const matrix of localMatrices) {
        await createMatrix(
          matrix as Omit<TourClassVehicleMatrix, 'id' | 'vehicleTypeName' | 'tourBundleName'>
        ).unwrap();
      }

      enqueueSnackbar(
        `${translate('tourClassVehicleMatrix.allSavedSuccess', { count: localMatrices.length })}`,
        { variant: 'success' }
      );
      setLocalMatrices([]);
    } catch {
      enqueueSnackbar(`${translate('tourClassVehicleMatrix.saveError')}`, { variant: 'error' });
    }
  };

  const handleRemoveFromList = (id: number) => {
    setLocalMatrices(localMatrices.filter((matrix) => matrix.id !== id));
    enqueueSnackbar(`${translate('tourClassVehicleMatrix.removedFromList')}`, {
      variant: 'info',
    });
  };

  const handleDelete = async () => {
    if (!matrixToDelete) {
      return;
    }

    try {
      await deleteMatrix(matrixToDelete.id).unwrap();
      setMatrixToDelete(null);
      enqueueSnackbar(`${translate('tourClassVehicleMatrix.deleteSuccess')}`, {
        variant: 'success',
      });
    } catch {
      enqueueSnackbar(`${translate('tourClassVehicleMatrix.deleteError')}`, { variant: 'error' });
    }
  };

  const handleCostFeeChange = (index: number, value: string) => {
    const newCostFee = [...(formData.costFee || [0, 0, 0, 0, 0])];
    const numValue = parseFloat(value);
    newCostFee[index] = isNaN(numValue) ? 0 : numValue;
    setFormData({ ...formData, costFee: newCostFee });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const matrixList = (matrices || []).slice().sort((a, b) => {
    const orderA = costTypeOrder[a.tourClassCostType] ?? Number.MAX_SAFE_INTEGER;
    const orderB = costTypeOrder[b.tourClassCostType] ?? Number.MAX_SAFE_INTEGER;

    if (orderA !== orderB) {
      return orderA - orderB;
    }

    return (a.vehicleTypeName || '').localeCompare(b.vehicleTypeName || '');
  });

  return (
    <Box sx={{ width: '70%', maxWidth: '100%' }}>
      <Stack spacing={3}>
        {/* Vehicle Cost Fees Management */}
        <VehicleCostFeesManagement tourBundleId={tourBundleId} />

        {/* Time category management */}
        <TourClassTimesManagement
          tourBundleId={tourBundleId}
          onTourClassTimesChange={handleTourClassTimesChange}
        />

        {/* Time category vehicle matrix */}
        <Card sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Typography variant="h6">{`${translate('tourClassVehicleMatrix.title')}`}</Typography>
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={() => handleOpenDialog()}
              disabled={!hasTourClassTimes}
              sx={{ textTransform: 'none' }}
            >
              {`${translate('tourClassVehicleMatrix.new')}`}
            </Button>
          </Stack>

          {!hasTourClassTimes && (
            <WarningBox title={`${translate('tourClassVehicleMatrix.noTourClassTimesWarning')}`} />
          )}

          <InfoBox title={`${translate('tourClassVehicleMatrix.infoDescription')}`} />

          {/* Unsaved local matrices */}
          {localMatrices.length > 0 && (
            <>
              <Typography variant="subtitle1" sx={{ mb: 2, color: 'warning.main' }}>
                {`${translate('tourClassVehicleMatrix.unsavedEntries')}`}
              </Typography>
              <TableContainer component={Paper} sx={{ mb: 3, backgroundColor: 'warning.lighter' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{`${translate('tourClassVehicleMatrix.costType')}`}</TableCell>
                      <TableCell>{`${translate('tourClassVehicleMatrix.vehicleType')}`}</TableCell>
                      <TableCell align="center">{`${translate('tourClassTimes.class', { number: 1 })}`}</TableCell>
                      <TableCell align="center">{`${translate('tourClassTimes.class', { number: 2 })}`}</TableCell>
                      <TableCell align="center">{`${translate('tourClassTimes.class', { number: 3 })}`}</TableCell>
                      <TableCell align="center">{`${translate('tourClassTimes.class', { number: 4 })}`}</TableCell>
                      <TableCell align="center">{`${translate('tourClassTimes.class', { number: 5 })}`}</TableCell>
                      <TableCell align="center">{`${translate('actions')}`}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {localMatrices.map((matrix) => (
                      <TableRow key={matrix.id}>
                        <TableCell>
                          {`${translate(`tourClassVehicleMatrix.types.${matrix.tourClassCostType}`)}`}
                        </TableCell>
                        <TableCell>{matrix.vehicleTypeName}</TableCell>
                        {matrix.costFee?.map((fee, index) => (
                          <TableCell key={index} align="center">
                            {fee.toFixed(2)} €
                          </TableCell>
                        ))}
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveFromList(matrix.id!)}
                            color="error"
                          >
                            <Iconify icon="eva:trash-2-outline" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  startIcon={<Iconify icon="eva:save-fill" />}
                  onClick={handleSaveAll}
                  sx={{ mb: 2, textTransform: 'none' }}
                >
                  {`${translate('tourClassVehicleMatrix.saveAll')}`}
                </Button>
              </Stack>
            </>
          )}

          {/* Saved matrices */}
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            {`${translate('tourClassVehicleMatrix.existingMatrix')}`}
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{`${translate('tourClassVehicleMatrix.costType')}`}</TableCell>
                  <TableCell>{`${translate('tourClassVehicleMatrix.vehicleType')}`}</TableCell>
                  <TableCell align="center">{`${translate('tourClassTimes.class', { number: 1 })}`}</TableCell>
                  <TableCell align="center">{`${translate('tourClassTimes.class', { number: 2 })}`}</TableCell>
                  <TableCell align="center">{`${translate('tourClassTimes.class', { number: 3 })}`}</TableCell>
                  <TableCell align="center">{`${translate('tourClassTimes.class', { number: 4 })}`}</TableCell>
                  <TableCell align="center">{`${translate('tourClassTimes.class', { number: 5 })}`}</TableCell>
                  <TableCell align="center">{`${translate('actions')}`}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {matrixList.length > 0 ? (
                  matrixList.map((matrix) => (
                    <TableRow key={matrix!.id}>
                      <TableCell>
                        {`${translate(`tourClassVehicleMatrix.types.${matrix!.tourClassCostType}`)}`}
                      </TableCell>
                      <TableCell>{matrix!.vehicleTypeName}</TableCell>
                      {matrix!.costFee.map((fee, index) => (
                        <TableCell key={index} align="center">
                          {fee.toFixed(2)} €
                        </TableCell>
                      ))}
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(matrix!)}
                            color="primary"
                          >
                            <Iconify icon="eva:edit-fill" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => setMatrixToDelete(matrix)}
                            color="error"
                          >
                            <Iconify icon="eva:trash-2-outline" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {`${translate('tourClassVehicleMatrix.noData')}`}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Entity Cost Fee Management */}
        <EntityCostFeeManagement tourBundleId={tourBundleId} />
      </Stack>

      {/* Dialog for creating/editing matrix */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingMatrix
            ? `${translate('tourClassVehicleMatrix.edit')}`
            : `${translate('tourClassVehicleMatrix.new')}`}
        </DialogTitle>
        <DialogContent>
          <Grid2 container spacing={2} sx={{ mt: 1 }}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>{`${translate('tourClassVehicleMatrix.costType')}`}</InputLabel>
                <Select
                  value={formData.tourClassCostType || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      tourClassCostType: e.target.value as TourClassCostType,
                    })
                  }
                  label={`${translate('tourClassVehicleMatrix.costType')}`}
                >
                  {costTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {`${translate(`tourClassVehicleMatrix.types.${type}`)}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>{`${translate('tourClassVehicleMatrix.vehicleType')}`}</InputLabel>
                <Select
                  value={formData.vehicleTypeId || ''}
                  label={`${translate('tourClassVehicleMatrix.vehicleType')}`}
                  onChange={(e) =>
                    setFormData({ ...formData, vehicleTypeId: Number(e.target.value) })
                  }
                >
                  <MenuItem value="" disabled>
                    {`${translate('tourClassVehicleMatrix.selectVehicleType')}`}
                  </MenuItem>
                  {vehicleTypes?.map((vt) => (
                    <MenuItem key={vt.id} value={vt.id}>
                      {vt.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid2>

            <Grid2 size={12}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                {`${translate('tourClassVehicleMatrix.costFee')}`}
              </Typography>
            </Grid2>

            {[1, 2, 3, 4, 5].map((classNum, index) => (
              <Grid2 key={classNum} size={{ xs: 12, sm: 6, md: 2.4 }}>
                <TextField
                  fullWidth
                  label={`${translate('tourClassTimes.class', { number: classNum })}`}
                  type="number"
                  value={formData.costFee?.[index] || 0}
                  onChange={(e) => handleCostFeeChange(index, e.target.value)}
                  InputProps={{
                    endAdornment: '€',
                  }}
                />
              </Grid2>
            ))}
          </Grid2>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} sx={{ textTransform: 'none' }}>
            {`${translate('cancel')}`}
          </Button>
          <Button onClick={handleSave} variant="contained" sx={{ textTransform: 'none' }}>
            {`${translate('save')}`}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={matrixToDelete !== null}
        title={`${translate('delete')}`}
        content={`${translate('tourClassVehicleMatrix.deleteConfirm')}`}
        action={
          <Button variant="contained" color="error" loading={isDeleting} onClick={handleDelete}>
            {`${translate('delete')}`}
          </Button>
        }
        onClose={() => {
          if (!isDeleting) {
            setMatrixToDelete(null);
          }
        }}
      />
    </Box>
  );
}
